import React, { useEffect, useRef, useState } from 'react';
import './EngineFlow.css';
import e1 from '../../assets/images/engine/engine-1.png';
import e2 from '../../assets/images/engine/engine-2.png';
import e3 from '../../assets/images/engine/engine-3.png';
import e4 from '../../assets/images/engine/engine-4.png';
import e5 from '../../assets/images/engine/engine-5.png';

export default function EngineFlow() {
  const gridRef = useRef(null);
  const refs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
  };
  const [svgData, setSvgData] = useState({ width: 0, height: 0, paths: [] });
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    function compute() {
      const grid = gridRef.current;
      if (!grid) return;
      const gRect = grid.getBoundingClientRect();

      // get absolute rects for each card (use the card container so edges are card edges)
      const rects = {};
      for (let i = 1; i <= 5; i++) {
        const el = refs[i].current;
        if (!el) return;
        rects[i] = el.getBoundingClientRect();
      }

      // helper: compute edge intersection point on rect toward target (absolute coords)
      function edgePoint(rect, target, padding = 18, inward = false) {
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = target.x - cx;
        const dy = target.y - cy;
        const candidates = [];
        // left/right sides
        if (dx !== 0) {
          const tLeft = (rect.left - cx) / dx;
          const yLeft = cy + tLeft * dy;
          if (tLeft > 0 && yLeft >= rect.top && yLeft <= rect.top + rect.height) candidates.push({ t: tLeft, x: rect.left, y: yLeft });
          const tRight = (rect.left + rect.width - cx) / dx;
          const yRight = cy + tRight * dy;
          if (tRight > 0 && yRight >= rect.top && yRight <= rect.top + rect.height) candidates.push({ t: tRight, x: rect.left + rect.width, y: yRight });
        }
        // top/bottom
        if (dy !== 0) {
          const tTop = (rect.top - cy) / dy;
          const xTop = cx + tTop * dx;
          if (tTop > 0 && xTop >= rect.left && xTop <= rect.left + rect.width) candidates.push({ t: tTop, x: xTop, y: rect.top });
          const tBottom = (rect.top + rect.height - cy) / dy;
          const xBottom = cx + tBottom * dx;
          if (tBottom > 0 && xBottom >= rect.left && xBottom <= rect.left + rect.width) candidates.push({ t: tBottom, x: xBottom, y: rect.top + rect.height });
        }
        if (candidates.length === 0) {
          // fallback to center edge
          const fallback = { x: cx + (dx === 0 ? 0 : Math.sign(dx) * (rect.width / 2)), y: cy + (dy === 0 ? 0 : Math.sign(dy) * (rect.height / 2)) };
          const vx = target.x - fallback.x;
          const vy = target.y - fallback.y;
          const dist = Math.hypot(vx, vy) || 1;
          return inward
            ? { x: fallback.x - (Math.max(8, padding) * (vx / dist)), y: fallback.y - (Math.max(8, padding) * (vy / dist)) }
            : { x: fallback.x + (padding * (vx / dist)), y: fallback.y + (padding * (vy / dist)) };
        }
        candidates.sort((a, b) => a.t - b.t);
        const p = candidates[0];
        const vx = target.x - p.x;
        const vy = target.y - p.y;
        const dist = Math.hypot(vx, vy) || 1;
        // move point slightly outward from rect along vector to target, or inward if requested
        return inward
          ? { x: p.x - (Math.max(8, padding) * (vx / dist)), y: p.y - (Math.max(8, padding) * (vy / dist)) }
          : { x: p.x + (padding * (vx / dist)), y: p.y + (padding * (vy / dist)) };
      }

      // compute start/end points for each path in absolute coordinates then convert to grid-relative
      const toAbsCenter = (r) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
      // For each connection, compute start outside source and end just outside target but slightly inset
      const a1 = edgePoint(rects[1], toAbsCenter(rects[2]), 18, false);
      const b1 = edgePoint(rects[2], toAbsCenter(rects[1]), 10, true);
      const a2 = edgePoint(rects[2], toAbsCenter(rects[3]), 18, false);
      const b2 = edgePoint(rects[3], toAbsCenter(rects[2]), 10, true);
      const a3 = edgePoint(rects[3], toAbsCenter(rects[4]), 18, false);
      const b3 = edgePoint(rects[4], toAbsCenter(rects[3]), 10, true);
      const a4 = edgePoint(rects[4], toAbsCenter(rects[5]), 18, false);
      const b4 = edgePoint(rects[5], toAbsCenter(rects[4]), 10, true);

      // Ensure each path goes from source (A) to target (B).
      const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);
      const s1 = toAbsCenter(rects[1]);
      const s2 = toAbsCenter(rects[2]);
      const s3 = toAbsCenter(rects[3]);
      const s4 = toAbsCenter(rects[4]);
      const s5 = toAbsCenter(rects[5]);

      // if B is closer to the source center than A, swap them
      if (dist(s1, b1) < dist(s1, a1)) { const tmp = a1; a1 = b1; b1 = tmp; }
      if (dist(s2, b2) < dist(s2, a2)) { const tmp = a2; a2 = b2; b2 = tmp; }
      if (dist(s3, b3) < dist(s3, a3)) { const tmp = a3; a3 = b3; b3 = tmp; }
      if (dist(s4, b4) < dist(s4, a4)) { const tmp = a4; a4 = b4; b4 = tmp; }

      // helper to make smooth cubic curve between absolute points
      const makePathAbs = (A, B) => {
        const dx = Math.abs(B.x - A.x);
        const dy = Math.abs(B.y - A.y);
        if (dx > dy) {
          const mx = (A.x + B.x) / 2;
          return `M ${A.x - gRect.left},${A.y - gRect.top} C ${mx - gRect.left},${A.y - gRect.top} ${mx - gRect.left},${B.y - gRect.top} ${B.x - gRect.left},${B.y - gRect.top}`;
        }
        const my = (A.y + B.y) / 2;
        return `M ${A.x - gRect.left},${A.y - gRect.top} C ${A.x - gRect.left},${my - gRect.top} ${B.x - gRect.left},${my - gRect.top} ${B.x - gRect.left},${B.y - gRect.top}`;
      };

      const paths = [
        makePathAbs(a1, b1),
        makePathAbs(a2, b2),
        makePathAbs(a3, b3),
        makePathAbs(a4, b4),
      ];

      setSvgData({ width: Math.max(300, Math.round(gRect.width)), height: Math.max(200, Math.round(gRect.height)), paths });
    }

    compute();
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, { passive: true });
    const obs = new ResizeObserver(onResize);
    if (gridRef.current) obs.observe(gridRef.current);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
      if (obs && gridRef.current) obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // reveal animation when engine grid enters viewport
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => setVisible(true), 250);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div className={`engine-flow ${visible ? 'visible' : ''}`} ref={containerRef} aria-hidden={!visible}>
      <div className="engine-grid" ref={gridRef}>
        <div className="ef-card ef-1" ref={refs[1]} role="article" aria-labelledby="ef1-title">
          <span className="ef-badge">1</span>
          <div className="ef-media"><img src={e1} alt="" /></div>
          <div className="ef-title" id="ef1-title">Validated mAb Library</div>
          <div className="ef-desc">Strategic access to a large, curated panel of validated monoclonal antibodies across diverse oncology targets.</div>
          <div className="ef-cta">No antibody generation from scratch</div>
        </div>

        <div className="ef-card ef-2" ref={refs[2]} role="article" aria-labelledby="ef2-title">
          <span className="ef-badge">2</span>
          <div className="ef-media"><img src={e2} alt="" /></div>
          <div className="ef-title" id="ef2-title">High-Throughput Screening</div>
          <div className="ef-desc">In-house capabilities to rapidly screen 10-15 clones per target for binding and specificity.</div>
          <div className="ef-cta">ELISA, Flow, IHC validation</div>
        </div>

        <div className="ef-card ef-3" ref={refs[3]} role="article" aria-labelledby="ef3-title">
          <span className="ef-badge">3</span>
          <div className="ef-media"><img src={e3} alt="" /></div>
          <div className="ef-title" id="ef3-title">AI-Powered Humanization</div>
          <div className="ef-desc">In-house AI-assisted pipeline for rapid humanization and sequence optimization — weeks, not months.</div>
          <div className="ef-cta">Weeks, not months</div>
        </div>

        <div className="ef-card ef-4" ref={refs[4]} role="article" aria-labelledby="ef4-title">
          <span className="ef-badge">4</span>
          <div className="ef-media"><img src={e4} alt="" /></div>
          <div className="ef-title" id="ef4-title">Functional Validation</div>
          <div className="ef-desc">Internalization and developability assays testing for lead selection to de-risk early development.</div>
          <div className="ef-cta">De-risk early development</div>
        </div>

        <div className="ef-card ef-5" ref={refs[5]} role="article" aria-labelledby="ef5-title">
          <span className="ef-badge">5</span>
          <div className="ef-media"><img src={e5} alt="" /></div>
          <div className="ef-title" id="ef5-title">Lead Candidate</div>
          <div className="ef-desc">Optimized, validated antibody ready for ADC conjugation and IND-enabling studies.</div>
          <div className="ef-cta">Clinical-ready asset</div>
        </div>

      </div>
    </div>
  );
}

